"use client";

import React, { useState } from "react";
import { 
  Dumbbell, 
  Calendar, 
  Clock, 
  Activity, 
  UserCheck, 
  ArrowRight, 
  Sparkles,
  ShieldCheck,
  CreditCard
} from "lucide-react";
import { GymMemberLead } from "@/lib/demos/gymDb";

interface GymKanbanProps {
  members: GymMemberLead[];
  onSelectMember: (m: GymMemberLead) => void;
  onUpdateStage: (memberId: number, newStage: GymMemberLead["stage"]) => void;
  currency?: "INR" | "USD";
}

interface StageConfig {
  id: GymMemberLead["stage"];
  title: string;
  dotColor: string;
  description: string;
}

const GYM_STAGES: StageConfig[] = [
  {
    id: "Trial Pass Inquired",
    title: "Trial Pass Inquired",
    dotColor: "bg-blue-400",
    description: "New web VIP 3-day pass inquiries",
  },
  {
    id: "Workout & Tour Scheduled",
    title: "Workout & Tour Scheduled",
    dotColor: "bg-amber-400",
    description: "Orientation set & VIP pass PDF dispatched",
  },
  {
    id: "Assessment Completed",
    title: "Assessment Completed",
    dotColor: "bg-indigo-400",
    description: "InBody 770 scan & movement screen",
  },
  {
    id: "Membership Proposed",
    title: "Membership Proposed",
    dotColor: "bg-orange-400",
    description: "Tier proposal presented to athlete",
  },
  {
    id: "Active Member (Won)",
    title: "Active Member (Won)",
    dotColor: "bg-emerald-400",
    description: "Annual dues paid & tax agreement issued",
  },
  {
    id: "Cancelled / Inactive",
    title: "Cancelled / Inactive",
    dotColor: "bg-stone-500",
    description: "Pass expired or schedule pause",
  },
];

export default function GymKanban({
  members,
  onSelectMember,
  onUpdateStage,
  currency = "INR",
}: GymKanbanProps) {
  const [draggedId, setDraggedId] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, id: number) => {
    e.dataTransfer.setData("text/plain", id.toString());
    setDraggedId(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetStage: GymMemberLead["stage"]) => {
    e.preventDefault();
    const idStr = e.dataTransfer.getData("text/plain");
    const id = parseInt(idStr, 10);
    if (!isNaN(id)) {
      onUpdateStage(id, targetStage);
    }
    setDraggedId(null);
  };

  const sym = currency === "USD" ? "$" : "₹";

  return (
    <div className="flex gap-4 overflow-x-auto pb-6 pt-2 select-none scrollbar-thin scrollbar-thumb-stone-800">
      {GYM_STAGES.map((col) => {
        const colMembers = members.filter((m) => m.stage === col.id);
        const colDues = colMembers.reduce((sum, m) => sum + (m.annual_dues || 0), 0);

        return (
          <div
            key={col.id}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, col.id)}
            className="w-80 shrink-0 bg-stone-950/80 rounded-2xl border border-stone-800/90 flex flex-col max-h-[calc(100vh-230px)] shadow-xl"
          >
            {/* Header */}
            <div className="p-3.5 border-b border-stone-800/80 bg-stone-900/60 rounded-t-2xl space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${col.dotColor}`} />
                  <h3 className="text-xs font-bold text-white tracking-tight">{col.title}</h3>
                </div>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-stone-800 text-stone-300">
                  {colMembers.length}
                </span>
              </div>

              <div className="flex items-center justify-between text-[11px] text-stone-400">
                <span>{colMembers.length} Prospects</span>
                <span className="font-semibold text-amber-400">{sym}{colDues.toLocaleString()}</span>
              </div>
            </div>

            {/* Cards List */}
            <div className="p-3 overflow-y-auto space-y-3 flex-1 scrollbar-thin scrollbar-thumb-stone-800">
              {colMembers.length === 0 ? (
                <div className="h-28 border-2 border-dashed border-stone-800/70 rounded-xl flex items-center justify-center text-xs text-stone-400">
                  Drop member leads here
                </div>
              ) : (
                colMembers.map((member) => (
                  <div
                    key={member.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, member.id)}
                    onClick={() => onSelectMember(member)}
                    className="p-4 rounded-xl bg-stone-900/90 border border-stone-800 hover:border-amber-500/40 cursor-grab active:cursor-grabbing hover:shadow-lg transition-all duration-200 space-y-3 group"
                  >
                    {/* Top Row: Name & Tier Badge */}
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="text-xs font-bold text-white group-hover:text-amber-300 transition-colors">
                          {member.lead_name}
                        </h4>
                        <span className="text-[11px] text-stone-400 block truncate max-w-[170px]">
                          {member.email}
                        </span>
                      </div>

                      <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 shrink-0">
                        {member.membership_tier.split(" ")[0]}
                      </span>
                    </div>

                    {/* Schedule / Status box */}
                    <div className="p-2 bg-stone-950/80 rounded-lg border border-stone-800/80 text-[11px] text-stone-300 space-y-1">
                      <div className="flex items-center justify-between text-stone-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-stone-500" />
                          {member.tour_date || "Tour Pending"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-emerald-400 font-semibold truncate">
                        <Dumbbell className="w-3 h-3 shrink-0" />
                        <span className="truncate">{member.fitness_goal}</span>
                      </div>
                    </div>

                    {/* InBody Diagnostic / Benchmark */}
                    {member.inbody_score && (
                      <div className="text-[10px] text-indigo-300 bg-indigo-500/10 px-2 py-1 rounded truncate border border-indigo-500/20">
                        InBody: {member.inbody_score}
                      </div>
                    )}

                    {/* Coach & Dues */}
                    <div className="pt-2 border-t border-stone-800/80 flex items-center justify-between text-[11px]">
                      <span className="text-stone-400 truncate max-w-[160px]">
                        Coach: {member.assigned_coach?.split(" ")[0] || "Unassigned"}
                      </span>
                      <span className="font-bold text-amber-400 shrink-0">
                        {sym}{member.annual_dues.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
